import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  PreloadAllModules
} from '@angular/router';
import { CustomerProfileAdapter } from '@data/customer-profile/customer-profile.adapter';
import { ExpressPurchaseAdapter } from '@data/express-purchase/express-purchase.adapter';
import { FooterAdapter } from '@data/footer/footer.adapter';
import { provideHeaderService } from '@data/header/header.adapter';
import { environment } from '@environment/environment';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { CustomerProfileRepository } from '@repositories/customer-profile.repository';
import { basicAuthInterceptor } from '@shared/interceptors/basic-auth.interceptor';
import { RuntimeConfigService } from '@services/runtime-config/runtime-config.service';
import { ExpressPurchaseRepository } from '@repositories/express-purchase.repository';
import { FooterRepository } from '@repositories/footer.repository';
import { DateFormatPipe } from '@shared/pipe.date-format';
import { LanguageSwitchService } from '@shared/services/switch-language.service';
import { routes } from './app.routes';
import { ServerTranslateLoader } from './libs/i18n/translate-loader.server';
import { provideBasePath } from './utils/basepath.util';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ExpressPurchaseRepository, useClass: ExpressPurchaseAdapter },
    { provide: CustomerProfileRepository, useClass: CustomerProfileAdapter },
    { provide: FooterRepository, useClass: FooterAdapter },
    { provide: 'googleTagManagerId', useValue: environment.gtmKey },
    provideHttpClient(withFetch(), withInterceptors([basicAuthInterceptor])),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(PreloadAllModules)
    ),
    provideClientHydration(),
    provideTranslateService({
      loader: { provide: TranslateLoader, useClass: ServerTranslateLoader }
    }),
    provideAppInitializer(() => {
      // Configure available languages, default to 'id', then restore any
      // previously chosen language from localStorage (browser only).
      const translateService = inject(TranslateService);
      const languageSwitchService = inject(LanguageSwitchService);
      translateService.addLangs(LanguageSwitchService.availableLanguages);
      translateService.setDefaultLang(LanguageSwitchService.defaultLanguage);
      languageSwitchService.initLanguageFromLocalStorage();
    }),
    // Load runtime config (basic auth) before the first HTTP call.
    provideAppInitializer(() => inject(RuntimeConfigService).load()),
    DateFormatPipe,
    provideHeaderService(),
    provideBasePath()
  ]
};
